import { GetItineraryImportListItem } from '@orbit/api-types'

/** How long a finished import stays worth a banner. */
export const RECENT_TERMINAL_MS = 30 * 60 * 1000

/**
 * A successful import whose events have all been removed since: the server
 * counts live events only, so nothing is left to review.
 */
export const isClearedImport = (item: GetItineraryImportListItem): boolean =>
	item.status === 'Success' && item.createdEventCount === 0

/**
 * The import the itinerary banner speaks for: a running one first, else the
 * most recently finished one within the window — skipping a cleared success,
 * which has nothing left to review.
 */
export const pickRelevantImport = (
	items: GetItineraryImportListItem[],
	nowMs: number = Date.now(),
): GetItineraryImportListItem | undefined => {
	const sorted = items.slice().sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt)
	const active = sorted.find(item => item.status === 'Pending' || item.status === 'Processing')
	if (active) return active
	const nowSec = nowMs / 1000
	return sorted.find(
		item => (nowSec - item.lastUpdatedAt) * 1000 < RECENT_TERMINAL_MS && !isClearedImport(item),
	)
}

/**
 * The dismissal is kept per trip and holds one import id — the latest one
 * dismissed — so a refresh keeps that banner down while a newer import still
 * gets to speak, and storage never grows past one entry per trip.
 */
const dismissedKey = (tripId: string): string => `itinerary.importBanner.dismissed.${tripId}`

export const readDismissedImport = (tripId: string): string | null => {
	try {
		return localStorage.getItem(dismissedKey(tripId))
	} catch {
		return null
	}
}

export const rememberDismissedImport = (tripId: string, importId: string): void => {
	try {
		localStorage.setItem(dismissedKey(tripId), importId)
	} catch {
		// Private windows, blocked or full storage: the banner just comes back.
	}
}

/**
 * The import the banner actually shows: the relevant one, unless it is the one
 * already dismissed on this browser.
 */
export const pickBannerImport = (
	items: GetItineraryImportListItem[],
	dismissedId: string | null,
	nowMs: number = Date.now(),
): GetItineraryImportListItem | undefined => {
	const relevant = pickRelevantImport(items, nowMs)
	return relevant && relevant.id === dismissedId ? undefined : relevant
}
